import { useCallback, useEffect, useMemo, useState } from "react";
import { Bike, Search, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { api } from "../lib/api";
import { normSearch } from "../lib/searchText";
import { PlatformPickDialog } from "./PlatformPickDialog";

const NONE = "__none";
const TONES = ["border-amber-200 bg-amber-50 text-amber-800", "border-orange-200 bg-orange-50 text-orange-800", "border-violet-200 bg-violet-50 text-violet-800",
  "border-sky-200 bg-sky-50 text-sky-800", "border-emerald-200 bg-emerald-50 text-emerald-800", "border-rose-200 bg-rose-50 text-rose-800"];
const RENTED = "__rented";
const inPlat = (e, p) => (p === RENTED ? e.declared.length > 1 : p === NONE ? !e.declared.length && !e.inferred.length : p === "__review" ? e.inferred.length > 0 : e.declared.includes(p) || e.inferred.includes(p));

const Row = ({ e, p, canEdit, onPick }) => {
  const secondary = p !== NONE && e.declared.length > 1 && e.declared[0] !== p && e.declared.includes(p);
  return (
    <button type="button" disabled={!canEdit} onClick={() => onPick(e)} data-testid={`platform-emp-${e.id}`}
      className="flex w-full items-center gap-2 rounded-lg border border-slate-100 px-3 py-2 text-right hover:bg-blue-50/60 disabled:cursor-default">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-slate-800">{e.name}</p>
        <p className="text-[10px] text-slate-400">{e.rider_id ? `#${e.rider_id}` : "بدون معرف"}{e.branch ? ` · ${e.branch}` : ""}{e.declared.length ? ` · ${e.declared.join("، ")}` : ""}</p>
      </div>
      {secondary && <span className="shrink-0 rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-bold text-violet-700">حساب فري لانسر{e.rented?.[p]?.owner_name ? ` · ${e.rented[p].owner_name}` : ""}</span>}
      {p === RENTED && (
        <span className="shrink-0 text-left text-[10px] leading-4 text-violet-700" data-testid={`rented-row-${e.id}`}>
          {e.declared.slice(1).map((pl) => <span key={pl} className="block">{pl}: {e.rented?.[pl]?.owner_name || <b className="text-red-600">بدون صاحب</b>}{e.rented?.[pl]?.owner_id ? ` #${e.rented[pl].owner_id}` : ""}</span>)}
        </span>)}
      {e.inferred.length > 0 && <span className="shrink-0 rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-bold text-sky-700" title={e.inferred.join("، ")}>يحتاج تأكيد</span>}
    </button>
  );
};

export const PlatformBoxes = ({ canEdit }) => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const [q, setQ] = useState("");
  const [pick, setPick] = useState(null);
  const load = useCallback(() => api.infoPlatforms().then(setData).catch(() => setData({ platforms: [], employees: [] })), []);
  useEffect(() => { load(); }, [load]);
  const emps = data?.employees || [];
  const list = useMemo(() => {
    if (!open) return [];
    const n = normSearch(q);
    return emps.filter((e) => inPlat(e, open) && (!n || normSearch(`${e.name} ${e.rider_id} ${e.branch}`).includes(n)));
  }, [open, q, emps]);
  const review = emps.filter((e) => e.inferred.length).length;
  const title = open === RENTED ? "سجل الحسابات المستأجرة" : open === NONE ? "بدون منصة" : open === "__review" ? "مناديب يحتاجون تأكيد المنصة" : `مناديب ${open}`;
  if (!data) return null;
  return (
    <section data-testid="platform-boxes">
      <h2 className="mb-3 flex items-center gap-2 font-heading text-lg font-bold text-slate-800"><Bike className="h-5 w-5 text-blue-600" /> منصات عمل المناديب</h2>
      {review > 0 && (
        <button type="button" onClick={() => { setQ(""); setOpen("__review"); }} data-testid="platform-review-banner"
          className="mb-3 flex w-full items-center gap-2 rounded-xl border border-sky-200 bg-sky-50 px-3 py-2 text-right text-xs font-semibold text-sky-800">
          <AlertTriangle className="h-4 w-4 shrink-0" /> {review} مندوب يعملون على منصات غير مسجّلة في ملفهم (من ملفات الأداء) — اضغط للمراجعة والتأكيد
        </button>
      )}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {[...data.platforms, NONE, RENTED].map((p, i) => (
          <button key={p} type="button" onClick={() => { setQ(""); setOpen(p); }} data-testid={`platform-box-${p}`}
            className={`rounded-2xl border p-4 text-right transition-transform hover:-translate-y-0.5 ${p === RENTED ? "border-violet-300 bg-violet-50 text-violet-800" : p === NONE ? "border-slate-200 bg-white text-slate-600" : TONES[i % TONES.length]}`}>
            <p className="text-xs font-semibold">{p === RENTED ? "الحسابات المستأجرة" : p === NONE ? "بدون منصة" : `مناديب ${p}`}</p>
            <p className="mt-1 text-2xl font-bold tabular-nums" data-testid={`platform-count-${p}`}>{emps.filter((e) => inPlat(e, p)).length}</p>
          </button>
        ))}
      </div>
      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent className="max-h-[85vh] max-w-md overflow-y-auto" dir="rtl" data-testid="platform-list-dialog">
          <DialogHeader className="text-right">
            <DialogTitle>{title} ({list.length})</DialogTitle>
            <DialogDescription>{canEdit ? "اضغط على المندوب لتحديد منصاته." : "قائمة المناديب"}</DialogDescription>
          </DialogHeader>
          <div className="relative">
            <Search className="absolute right-3 top-2.5 h-4 w-4 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="ابحث بالاسم أو المعرف أو الفرع…" className="pr-9" data-testid="platform-list-search" />
          </div>
          <div className="space-y-1.5">
            {list.length ? list.map((e) => <Row key={e.id} e={e} p={open} canEdit={canEdit} onPick={setPick} />)
              : <p className="py-6 text-center text-sm text-slate-400">لا يوجد مناديب</p>}
          </div>
        </DialogContent>
      </Dialog>
      <PlatformPickDialog emp={pick} platforms={data.platforms} onClose={() => setPick(null)} onSaved={load} />
    </section>
  );
};
